import { Avatar, HStack, Input, Link, Text, VStack } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import React from "react";
import type { CaregiverId } from "../../../../../../shared/schema/schema";
import SharedSelect from "../../../../../../shared/components/Select";
import { dateFormatter } from "../../../../../../shared/utils/date-formatter";
import PatientProfileLookupSelect, {
  EntityProfileLookupOption as PatientProfileLookupOption,
} from "../../../../../entityProfileModal/components/EntityProfileLookupSelect";
import PatientProfileInlineEditableRow from "../../../../../entityProfileModal/components/EntityProfileInlineEditableRow";
import {
  EntityProfileSummaryBlock as SummaryBlock,
  EntityProfileSummaryRow as SummaryRow,
} from "../../../../../entityProfileModal/components/EntityProfileSummary";
import { entityProfileModalColors } from "../../../../../entityProfileModal/entityProfileModal.theme";
import type { PatientProfileBridgeActions } from "../../../../patientProfileModal.actions";
import { PatientProfileDisplayData } from "../../../../patientProfileModal.mocks";
import { PatientProfilePatient } from "../../../../patientProfileModal.types";
import type { SummaryDraft, SummaryEditableField } from "../PatientProfileSummarySection.hooks";
import EntityModalButton from "../../../../../entityProfileModal/components/EntityModalButton";

type SelectOption<TValue> = {
  label: string;
  value: TValue;
};

export type PatientProfileSummaryServicesProps = {
  actions: PatientProfileBridgeActions;
  activeDraft: SummaryDraft;
  activeField: SummaryEditableField | null;
  agencyBranchOptions: Array<SelectOption<SummaryDraft["agencyBranchIds"][number]>>;
  agencyBranchesText: string;
  agencyTeamName: string;
  agencyTeamOptions: Array<SelectOption<number>>;
  blacklistedCaregivers: PatientProfilePatient["blacklisted"];
  blacklistedCaregiversText: string;
  canRunEligibilityCheck: boolean;
  caregiverOptions: ReadonlyArray<{
    certifications?: readonly string[] | null;
    displayName?: string | null;
    id: CaregiverId;
    status?: string | null;
  }>;
  displayData: Pick<PatientProfileDisplayData, "assignedCoordinatorName" | "currentOfficeName">;
  isAgencyBranchesLoading: boolean;
  isAgencyTeamsLoading: boolean;
  isBlacklistLoading: boolean;
  isBlacklistSaving: boolean;
  isEligibilityCheckPending: boolean;
  isServicesInformationEditing: boolean;
  patient: PatientProfilePatient;
  shouldShowEligibility: boolean;
  summaryDraft: SummaryDraft;
  onActivateField: (field: SummaryEditableField) => void;
  onCancelField: () => void;
  onCommitField: () => void;
  onOpenCertificationPeriod: () => void;
  onRunEligibilityCheck: () => void;
  onUpdateBlacklist: (caregiverIds: NonNullable<PatientProfilePatient["blacklisted"]>) => void;
  onUpdateField: <TKey extends keyof SummaryDraft>(key: TKey, value: SummaryDraft[TKey]) => void;
};

const wageParityOptions = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

function SummaryEditableRow(props: {
  activeField: SummaryEditableField | null;
  children: React.ReactNode;
  cancelOnBlur?: boolean;
  field: SummaryEditableField;
  label: string;
  value: React.ReactNode;
  onActivate: (field: SummaryEditableField) => void;
  onCancel: () => void;
  onCommit: () => void;
}) {
  return (
    <PatientProfileInlineEditableRow
      activeField={props.activeField}
      cancelOnBlur={props.cancelOnBlur}
      field={props.field}
      label={props.label}
      value={props.value}
      onActivate={props.onActivate}
      onCancel={props.onCancel}
      onCommit={props.onCommit}
    >
      {props.children}
    </PatientProfileInlineEditableRow>
  );
}

function formatDraftDateValue(value: string) {
  return value === "" ? "" : dateFormatter.toDate(LocalDate.parse(value));
}

function formatEligibility(patient: PatientProfilePatient) {
  if (patient.eligibiltyStatus === null) {
    return "No Data";
  }

  if (patient.eligibiltyStatus.isEligible === null) {
    return "Unknown";
  }

  return patient.eligibiltyStatus.isEligible ? "Eligible" : "Not eligible";
}

function formatEligibilitySummary(patient: PatientProfilePatient) {
  if (patient.eligibiltyStatus === null) {
    return "No Data";
  }

  const parts = [`Status: ${formatEligibility(patient)}`];

  if (patient.eligibiltyStatus.createdAt !== null) {
    parts.push(`Last update: ${dateFormatter.toDateTime(patient.eligibiltyStatus.createdAt)}`);
  }

  return parts.join(" | ");
}

function formatSummaryValue(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (value instanceof LocalDate) {
    return dateFormatter.toDate(value);
  }

  return String(value);
}

function formatWageParity(value: PatientProfilePatient["wageParity"]) {
  if (value === null || value === undefined) {
    return "";
  }

  return value ? "Yes" : "No";
}

function getAgencyMemberLookupOption(id: number | null, displayName: string) {
  return id === null ? null : { id, displayName: displayName || `Agency member #${id}` };
}

function getOfficeLookupOption(
  id: number | null,
  displayName: string
): PatientProfileLookupOption | null {
  return id === null ? null : { id, displayName: displayName || `Office #${id}` };
}

function AssignedCaregiverValue(props: {
  actions: PatientProfileBridgeActions;
  caregiver: PatientProfilePatient["caregiverOnCase"] | PatientProfilePatient["nurseOnCase"];
}) {
  if (props.caregiver === null) {
    return null;
  }

  return (
    <HStack justifyContent="flex-end" minW={0} spacing={2}>
      <Avatar
        name={props.caregiver.fullName}
        size="xs"
        src={props.caregiver.photoUrl ?? undefined}
      />
      <Link
        color={entityProfileModalColors.activeMuted}
        fontWeight={700}
        onClick={() =>
          props.actions.navigateToCaregiver(props.caregiver!.caregiverId as CaregiverId)
        }
      >
        {props.caregiver.fullName}
      </Link>
    </HStack>
  );
}

function getBlacklistOptions(props: PatientProfileSummaryServicesProps) {
  const selectedIds = new Set(props.blacklistedCaregivers ?? []);
  const selectableCaregivers = props.caregiverOptions.filter((caregiver) => {
    const certifications = caregiver.certifications ?? [];

    return (
      selectedIds.has(caregiver.id) ||
      (caregiver.status === "ACTIVE" &&
        certifications.length > 0 &&
        certifications.every((certification) => certification === "HHA" || certification === "PCA"))
    );
  });

  return selectableCaregivers.map((caregiver) => ({
    label: caregiver.displayName ?? `Caregiver ID ${caregiver.id}`,
    value: caregiver.id,
  }));
}

export function PatientProfileSummaryServices(props: PatientProfileSummaryServicesProps) {
  const blacklistedCaregivers = props.blacklistedCaregivers ?? [];
  const blacklistOptions = getBlacklistOptions(props);
  const blacklistedCaregiversText =
    props.blacklistedCaregiversText ||
    (blacklistedCaregivers.length === 0
      ? ""
      : blacklistedCaregivers
          .map(
            (id) =>
              blacklistOptions.find((option) => option.value === id)?.label ?? `Caregiver ID ${id}`
          )
          .join(", "));

  return (
    <SummaryBlock title="Services Information">
      {!props.isServicesInformationEditing ? (
        <SummaryRow
          actions={
            <EntityModalButton kind="blue" size="sm" onClick={props.onOpenCertificationPeriod}>
              Certification period
            </EntityModalButton>
          }
          label="Start of care"
          value={formatSummaryValue(props.patient.startOfCare)}
        />
      ) : (
        <SummaryEditableRow
          activeField={props.activeField}
          field="startOfCare"
          label="Start of care"
          value={formatDraftDateValue(props.summaryDraft.startOfCare)}
          onActivate={props.onActivateField}
          onCancel={props.onCancelField}
          onCommit={props.onCommitField}
        >
          <Input
            autoFocus
            size="sm"
            type="date"
            value={props.activeDraft.startOfCare}
            onChange={(event) => props.onUpdateField("startOfCare", event.target.value)}
          />
        </SummaryEditableRow>
      )}
      {!props.isServicesInformationEditing ? (
        <SummaryRow
          label="Assigned Coordinator"
          value={props.displayData.assignedCoordinatorName}
        />
      ) : (
        <SummaryEditableRow
          activeField={props.activeField}
          cancelOnBlur={false}
          field="assignedCoordinator"
          label="Assigned Coordinator"
          value={props.displayData.assignedCoordinatorName}
          onActivate={props.onActivateField}
          onCancel={props.onCancelField}
          onCommit={props.onCommitField}
        >
          <PatientProfileLookupSelect
            entity="agency-members"
            label="Select coordinator"
            query={{ officeId: props.activeDraft.currentOfficeId ?? undefined, status: "Active" }}
            selectedOption={getAgencyMemberLookupOption(
              props.patient.assignedCoordinator,
              props.displayData.assignedCoordinatorName
            )}
            value={props.activeDraft.assignedCoordinator}
            onChange={(option) => props.onUpdateField("assignedCoordinator", option?.id ?? null)}
          />
        </SummaryEditableRow>
      )}
      {!props.isServicesInformationEditing ? (
        <SummaryRow label="Assigned Team" value={props.agencyTeamName} />
      ) : (
        <SummaryEditableRow
          activeField={props.activeField}
          cancelOnBlur={false}
          field="agencyTeamId"
          label="Assigned Team"
          value={props.agencyTeamName}
          onActivate={props.onActivateField}
          onCancel={props.onCancelField}
          onCommit={props.onCommitField}
        >
          <SharedSelect
            allowUnselect
            isLoading={props.isAgencyTeamsLoading}
            label="Select team"
            multiple={false}
            options={props.agencyTeamOptions}
            value={props.activeDraft.agencyTeamId}
            onChange={(value) => props.onUpdateField("agencyTeamId", value ?? null)}
          />
        </SummaryEditableRow>
      )}
      <SummaryRow
        label="Assigned Caregiver"
        value={
          <AssignedCaregiverValue
            actions={props.actions}
            caregiver={props.patient.caregiverOnCase}
          />
        }
      />
      <SummaryRow
        label="Assigned Nurse"
        value={
          <AssignedCaregiverValue actions={props.actions} caregiver={props.patient.nurseOnCase} />
        }
      />
      {!props.isServicesInformationEditing ? (
        <SummaryRow label="Caregivers Blacklist" value={blacklistedCaregiversText} />
      ) : (
        <SummaryRow
          label="Caregivers Blacklist"
          value={
            <VStack alignItems="flex-end" spacing={1}>
              <SharedSelect
                multiple
                isDisabled={props.isBlacklistSaving}
                isLoading={props.isBlacklistLoading || props.isBlacklistSaving}
                label="Select caregivers"
                options={blacklistOptions}
                selectedLabel={(caregiverIds) =>
                  caregiverIds
                    .map(
                      (id) =>
                        blacklistOptions.find((option) => option.value === id)?.label ??
                        `Caregiver ID ${id}`
                    )
                    .join(", ")
                }
                value={blacklistedCaregivers}
                onChange={(value) => props.onUpdateBlacklist(value ?? [])}
              />
              <Text color={entityProfileModalColors.muted} fontSize="12px">
                Active HHA/PCA caregivers only
              </Text>
            </VStack>
          }
        />
      )}
      {!props.isServicesInformationEditing ? (
        <SummaryRow label="Office" value={props.displayData.currentOfficeName} />
      ) : (
        <SummaryEditableRow
          activeField={props.activeField}
          cancelOnBlur={false}
          field="currentOfficeId"
          label="Office"
          value={props.displayData.currentOfficeName}
          onActivate={props.onActivateField}
          onCancel={props.onCancelField}
          onCommit={props.onCommitField}
        >
          <PatientProfileLookupSelect
            allowUnselect={false}
            entity="offices"
            label="Select office"
            query={{ status: "Active" }}
            selectedOption={getOfficeLookupOption(
              props.patient.currentOfficeId,
              props.displayData.currentOfficeName
            )}
            value={props.activeDraft.currentOfficeId}
            onChange={(option) => props.onUpdateField("currentOfficeId", option?.id ?? null)}
          />
        </SummaryEditableRow>
      )}
      {!props.isServicesInformationEditing ? (
        <SummaryRow label="Branches" value={props.agencyBranchesText} />
      ) : (
        <SummaryEditableRow
          activeField={props.activeField}
          cancelOnBlur={false}
          field="agencyBranchIds"
          label="Branches"
          value={props.agencyBranchesText}
          onActivate={props.onActivateField}
          onCancel={props.onCancelField}
          onCommit={props.onCommitField}
        >
          <SharedSelect
            multiple
            isLoading={props.isAgencyBranchesLoading}
            label="Select branches"
            options={props.agencyBranchOptions}
            selectedLabel={(selectedBranchIds) =>
              selectedBranchIds
                .map(
                  (branchId) =>
                    props.agencyBranchOptions.find((option) => option.value === branchId)?.label ??
                    `Branch #${branchId}`
                )
                .join(", ")
            }
            value={props.activeDraft.agencyBranchIds}
            onChange={(value) => props.onUpdateField("agencyBranchIds", value ?? [])}
          />
        </SummaryEditableRow>
      )}
      {!props.isServicesInformationEditing ? (
        <SummaryRow label="Wage Parity" value={formatWageParity(props.patient.wageParity)} />
      ) : (
        <SummaryEditableRow
          activeField={props.activeField}
          cancelOnBlur={false}
          field="wageParity"
          label="Wage Parity"
          value={formatWageParity(props.summaryDraft.wageParity)}
          onActivate={props.onActivateField}
          onCancel={props.onCancelField}
          onCommit={props.onCommitField}
        >
          <SharedSelect
            label="Select wage parity"
            multiple={false}
            options={wageParityOptions}
            value={props.activeDraft.wageParity}
            onChange={(value) => props.onUpdateField("wageParity", value ?? null)}
          />
        </SummaryEditableRow>
      )}
      {props.shouldShowEligibility && (
        <>
          <SummaryRow
            actions={
              props.canRunEligibilityCheck ? (
                <EntityModalButton
                  iconClassName="fa fa-refresh"
                  isLoading={props.isEligibilityCheckPending}
                  kind="blue"
                  size="sm"
                  style={{ minWidth: 194 }}
                  onClick={props.onRunEligibilityCheck}
                >
                  {props.patient.eligibiltyStatus === null ? "Check now" : "Re-check"}
                </EntityModalButton>
              ) : null
            }
            label="Eligibility"
            value={formatEligibilitySummary(props.patient)}
          />
          {props.patient.eligibiltyStatus?.isEligibleOnOtherPayer && (
            <SummaryRow
              label="Eligibility payer"
              value="Eligible, but not for any active contract"
            />
          )}
          {props.patient.eligibiltyStatus?.message && (
            <SummaryRow
              label="Eligibility message"
              value={props.patient.eligibiltyStatus.message}
            />
          )}
        </>
      )}
    </SummaryBlock>
  );
}
